/**
 * Shared constants + pure helpers for the file-interface modules.
 *
 * Split out from the monolithic file-interface.ts so each domain module
 * (crud / trash / versions) can import only what it needs without pulling in
 * every thunk in the file.
 */

import {
  convertISODateTime,
  givenTimeIsBeforeOther,
} from "@utils/time-helpers";
import { stringContainsSubstringsOfSet } from "@utils/string-helpers";
import { getStore, AppStoreLike } from "@store";
import commercialFmsAdapter from "@features/files/adapters/commercial-files";
import filesAdapter from "@features/files/adapters";
import type { FilesAdapter, FilesConfig, FileLocation } from "@types";

export type Store = AppStoreLike;

/**
 * Default query used by list / trash / search calls. Callers spread this and
 * override the fields they care about (status, pageLimit, file_location, …).
 */
export const initialFilesConfig: FilesConfig = {
  status: "live",
  pageLimit: 10,
  pageNumber: 1,
  page_order: "file_location_id",
  reverse: true,
};

/**
 * Returns the commercial FMS adapter for commercial clients and the standard
 * files adapter otherwise. Kept async to preserve the historical signature
 * (some callers `await` it via top-level `await`).
 */
export const getFileManagerAdapter = async (
  store: Store = getStore()
): Promise<FilesAdapter> => {
  const { isCommercialClient } = store.getState().config;
  if (isCommercialClient) return commercialFmsAdapter;
  else return filesAdapter;
};

export const timeEnrichmentBeforeView = (
  item: any,
  filesExtensionsToExclude?: string[]
): any => {
  const file_preview = stringContainsSubstringsOfSet(
    item.content_type,
    filesExtensionsToExclude
  );
  return {
    ...item,
    inputISOScheduledDate: convertISODateTime(item.scheduled_date)
      .toMomentWithDBTimeZone,
    inputISOLastUpdatedDate: convertISODateTime(item.last_updated)
      .toMomentWithDBTimeZone,
    file_preview: file_preview && file_preview.length === 0 ? item.url : null,
  };
};

/**
 * Deduplicates multi-file locations, keeping the entry with the latest
 * `scheduled_date` for each file_name, and enriches each file with the
 * display fields expected by the UI.
 */
export const transformFileLocation = (
  fileLocationList: any[],
  baseURL: string,
  filesExtensionsToExclude: string[]
): FileLocation[] => {
  const allFiles: any[] = [];
  if (fileLocationList) {
    fileLocationList.forEach((singleFileLocation) => {
      const files: any[] = [];
      if (singleFileLocation.multifile) {
        singleFileLocation.files?.forEach(
          (singleFile: Record<string, any>) => {
            if (files.length && singleFile.status === "live") {
              const duplicate = files.filter(
                (file) => file.file_name === singleFile.file_name
              );
              if (duplicate.length) {
                duplicate.forEach((dup) => {
                  if (
                    givenTimeIsBeforeOther(
                      dup.scheduled_date,
                      singleFile.scheduled_date
                    )
                  ) {
                    files.push(singleFile);
                    const index = files.indexOf(dup);
                    files.splice(index, 1);
                  }
                });
              } else files.push(singleFile);
            } else files.push(singleFile);
          }
        );
        allFiles.push({
          file_location_id: singleFileLocation.file_location_id,
          location_name: singleFileLocation.location_name,
          files: files,
          multifile: singleFileLocation.multifile,
          showFiles: false,
        });
      } else {
        allFiles.push(singleFileLocation);
      }
    });

    return allFiles.map((singleFileLocation) => ({
      ...singleFileLocation,
      display_file_location: `${baseURL}${singleFileLocation.location_name}`,
      files: singleFileLocation.files.map((singleFile: Record<string, any>) =>
        timeEnrichmentBeforeView(singleFile, filesExtensionsToExclude)
      ),
    }));
  } else return [];
};

/**
 * Strips a file to the shape the POST/PATCH endpoints expect. Sanitizes
 * accidental extra fields that may have accumulated on the client-side item.
 */
export const timeEnrichmentBeforeSend = (file: any): any => ({
  description: file.description,
  fileDetails: file.fileDetails,
  file_data: file.file_data,
  file_id: file.file_id,
  file_name: file.file_name,
  file_state: file.file_state,
  inputISOLastUpdatedDate: file.inputISOLastUpdatedDate,
  inputISOScheduledDate: file.inputISOScheduledDate,
  previousFileDetails: file.previousFileDetails,
  scheduled_date: file.scheduled_date,
  title: file.title,
  url: file.url,
});
